/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService, UtilService } from '@dm/core';
import { type Observable } from 'rxjs';

import { type VirtualMachine } from './virtual-machine';
import { VIRTUAL_MACHINE_SYSTEM_DISK_MAX_CAPACITY, VIRTUAL_MACHINE_SYSTEM_DISK_MIN_CAPACITY } from './virtual-machine.const';

@Injectable({
    providedIn: 'root',
})
export class VirtualMachineService {
    constructor(
        private httpService: HttpService,
        private utilService: UtilService,
    ) { }

    /**
     * 获取虚拟机状态 1.激活状态 2.初始化状态 3.开启状态 4.是否激活中
     */
    getSummaryStatus(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartcontainer.summaryStatus', 'V2');
        return this.httpService.get(rest, options);
    }

    /**
     * 创建虚拟机
     */
    createVirtualMachine(params: VirtualMachine, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vm.create', 'V2');

        return this.httpService.post(rest, params, options);
    }

    /**
     * 删除虚拟机
     */
    deleteVirtualMachine(name: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vm.delete', 'V2').format(name);

        return this.httpService.delete(rest, options);
    }

    /**
     * 批量查询虚拟机
     */
    getVirtualMachineBatch(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vm.getBatch', 'V2');

        return this.httpService.get(rest, options);
    }

    /**
     * 查询虚拟机
     */
    getVirtualMachine(name: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vm.get', 'V2').setParams({ name });

        return this.httpService.get(rest, options);
    }

    /**
     * 批量查询镜像
     */
    getImageDatas(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('ContainerRepository.getImageBatch');
        return this.httpService.get(rest, options);
    }

    /**
     * 获取连接虚拟机rest
     */
    getConectRest(virtualMachineName: string): Rest {
        return this.httpService.makeUrl('vm.conect', 'V2').format(virtualMachineName);
    }

    /**
     * 检验容量是否小于最小容量(10GB)
     */
    minCap(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) {
                return null;
            }
            const currentCap = this.utilService.adaptUnit(control.value.value, control.value.unit, 'Sector').value;

            return currentCap < VIRTUAL_MACHINE_SYSTEM_DISK_MIN_CAPACITY ? { minCap: true } : null;
        };
    }

    /**
     * 检验容量是否大于最大容量(10TB)
     */
    maxCap(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) {
                return null;
            }

            const currentCap = this.utilService.adaptUnit(control.value.value, control.value.unit, 'Sector').value;

            return currentCap > VIRTUAL_MACHINE_SYSTEM_DISK_MAX_CAPACITY ? { maxCap: true } : null;
        };
    }

    /**
     * 虚拟机名称校验（只能包含小写字母、数字、“-”和“.”，并且必须以小写字母或数字开头和结尾。）
     */
    nameValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            if (!control) {
                return null;
            }

            const valueStr: string = control.value.toString();
            const nameValidatorReg = /^(?:[A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;

            if (!valueStr || nameValidatorReg.test(valueStr)) {
                return null;
            } else {
                return { nameValidator: true };
            }
        };
    }
}

